import { SetNonNullable } from 'type-fest'

import { ARTIFACT_LABEL, ARTIFACT_LABELS, DATE_FORMATTER } from '../../constants'
import { documentUrl } from '../../functions'
import { RecommendedDocuments_DocumentFragment } from './__generated__/recommended-documents.graphql'

type Recommendation = SetNonNullable<
  NonNullable<NonNullable<RecommendedDocuments_DocumentFragment['recommendations']>[number]>
>

type ScoreDetails = {
  algorithmName: string
  algorithmScore: number
  algorithmWeight: number
}

type RecommendedDocument = {
  docId: number
  artifact: string
  title: string
  url: string
  description: string
  artifactLabel: string
  typeLabel: string
  formattedDate: string
  score: number
  publisherName: string
  scoreDetails: ScoreDetails[]
  hasQuote: boolean
}

export const RECOMMENDER_ID = 'default'
const PERSONA_RERANK = true

export const artifactLabel = (artifact: string) =>
  ARTIFACT_LABELS[artifact as keyof typeof ARTIFACT_LABELS]?.singularLabel ||
  (artifact[0] || '').toLocaleUpperCase() + artifact.slice(1).toLowerCase()

// PERF: We can improve this function legibility @lr-core-exp
export const getRecommendedDocuments = (
  document: RecommendedDocuments_DocumentFragment
): RecommendedDocument[] =>
  (document.recommendations || [])
    .filter((r): r is Recommendation => !!(r?.document?.url && r?.document?.title))
    .map(
      (r): RecommendedDocument => ({
        docId: r.document.docId,
        artifact: r.document.artifact,
        title: r.document.title || '',
        url: documentUrl(r.document.url || ''),
        description:
          (r.document.artifact === ARTIFACT_LABEL.SUMULA && r.document.summaryPrecedentStatement) ||
          r.document.description ||
          '',
        artifactLabel: artifactLabel(r.document.artifact),
        typeLabel:
          (r.document.artifact === ARTIFACT_LABEL.JURISPRUDENCIA && r.document.jurisTypeLabel) ||
          (r.document.artifact === ARTIFACT_LABEL.PECA && r.document.motionTypeLabel) ||
          '',
        formattedDate: (r.document.date && DATE_FORMATTER.format(r.document.date)) || '',
        publisherName: r.document.publisher?.fullname || '',
        score: r.score,
        scoreDetails: r.scoreDetails
          .filter(
            (s): s is ScoreDetails =>
              !!s?.algorithmName && !!s?.algorithmScore && !!s?.algorithmWeight
          )
          .map(({ algorithmName, algorithmScore, algorithmWeight }) => ({
            algorithmName,
            algorithmScore,
            algorithmWeight,
          })),
        hasQuote: r.document.type === 'ACORDAO',
      })
    )

export const getEventProps = (recommendedDocs: RecommendedDocument[], index = -1) => ({
  algorithm: RECOMMENDER_ID,
  documents: (index < 0 ? recommendedDocs : recommendedDocs.slice(index, index + 1)).map((doc) => ({
    docId: doc.docId,
    artifact: doc.artifact,
    score: doc.score,
    scoreDetails: doc.scoreDetails,
  })),
  metadata: {
    personaRerank: PERSONA_RERANK,
  },
  ...(index >= 0 ? { position: index } : undefined),
})
